'use client'

import { useState, useEffect } from 'react'
import { Property, User, ApplicationWithDetails } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'

interface Review {
  _id?: string
  id?: string
  reviewer?: User
  client_id?: string
  landlord?: User
  landlord_id?: string
  property?: Property
  property_id?: string
  rating: number
  title: string
  comment?: string
  review_text?: string
  isAnonymous?: boolean
  is_anonymous?: boolean
  isVerified?: boolean
  helpfulCount?: number
  helpful_count?: number
  createdAt?: string
  created_at?: string
  updated_at?: string
  landlordResponse?: {
    comment: string
    respondedAt: string
  }
}

interface ReviewRatingSystemProps {
  clientId: string
}

export default function ReviewRatingSystem({ clientId }: ReviewRatingSystemProps) {
  const { token, user } = useUser()
  const toast = useToast()
  const [reviews, setReviews] = useState<Review[]>([])
  const [applications, setApplications] = useState<ApplicationWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState('')
  const [rating, setRating] = useState(5)
  const [title, setTitle] = useState('')
  const [reviewText, setReviewText] = useState('')
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [filter, setFilter] = useState<'all' | 'my_reviews' | 'public_reviews'>('all')

  // Load reviews and applications
  const loadData = async () => {
    if (!token) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const apiUrl = getApiUrl()

      // Fetch user's reviews first to filter out already-reviewed properties
      const reviewsResponse = await fetch(`${apiUrl}/reviews/user/my-reviews`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      let currentReviews: Review[] = []
      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json()
        currentReviews = reviewsData.data || reviewsData || []
        setReviews(currentReviews)
      }

      // Fetch approved applications (properties user can review)
      const applicationsResponse = await fetch(`${apiUrl}/applications?status=approved`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (applicationsResponse.ok) {
        const appsData = await applicationsResponse.json()
        const appsArray = Array.isArray(appsData) ? appsData : (appsData.applications || [])
        
        // Filter to only approved applications that belong to the current user
        // and haven't been reviewed yet
        const userId = user?.id || (user as any)?._id || clientId
        const reviewedPropertyIds = new Set(
          currentReviews.map(r => {
            const propId = (r.property as any)?._id || (r.property as any)?.id || r.property_id
            return propId?.toString()
          }).filter(Boolean)
        )
        
        const validApplications = appsArray.filter((app: ApplicationWithDetails) => {
          // Check application status is approved or accepted
          const isApproved = app.status === 'approved' || app.status === 'accepted'
          
          // Check application belongs to current user
          const appClientId = (app.client as any)?._id || (app.client as any)?.id || (app as any).client_id || (app as any).client
          const belongsToUser = appClientId?.toString() === userId?.toString()
          
          // Check property exists
          const hasProperty = !!app.property
          
          // Check if property hasn't been reviewed yet
          const propertyId = (app.property as any)?._id || (app.property as any)?.id || (app as any).property_id
          const notReviewed = !reviewedPropertyIds.has(propertyId?.toString())
          
          return isApproved && belongsToUser && hasProperty && notReviewed
        })
        
        setApplications(validApplications)
      }
    } catch (error) {
      console.error('Error loading reviews:', error)
      toast.error('Failed to load reviews')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [clientId, user?.id, token])

  // Submit review
  const submitReview = async () => {
    if (!selectedApplication || !title || !reviewText) {
      toast.error('Please fill in all required fields')
      return
    }

    if (!token) {
      toast.error('Please log in to submit a review')
      return
    }

    if (!user) {
      toast.error('User information not available. Please refresh and try again.')
      return
    }

    try {
      const application = applications.find(app => (app.id || (app as any)._id) === selectedApplication)
      
      // Validate application exists
      if (!application || !application.property) {
        toast.error('Selected application not found.')
        return
      }

      // Validate application status is approved or accepted
      const isApproved = application.status === 'approved' || application.status === 'accepted'
      if (!isApproved) {
        toast.error('You can only review properties from approved applications.')
        return
      }

      // Validate application belongs to current user
      const userId = user.id || (user as any)._id || clientId
      const appClientId = (application.client as any)?._id || (application.client as any)?.id || (application as any).client_id || (application as any).client
      if (appClientId?.toString() !== userId?.toString()) {
        toast.error('You can only review properties from your own approved applications.')
        return
      }

      // Check if user has already reviewed this property
      const propertyId = (application.property as any)._id || (application.property as any).id || application.property
      const propertyIdStr = propertyId?.toString()
      const hasReviewed = reviews.some(r => {
        const reviewPropId = (r.property as any)?._id || (r.property as any)?.id || r.property_id
        return reviewPropId?.toString() === propertyIdStr
      })

      if (hasReviewed) {
        toast.error('You have already submitted a review for this property.')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          propertyId: propertyId,
          applicationId: selectedApplication,
          rating,
          title,
          comment: reviewText,
          isAnonymous
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit review')
      }

      // Add the new review to the reviews state immediately
      if (data.data) {
        const newReview: Review = {
          ...data.data,
          id: data.data._id || data.data.id,
          reviewer: data.data.reviewer || user,
          property: data.data.property || application.property,
          comment: data.data.comment || data.data.review_text,
          review_text: data.data.comment || data.data.review_text,
          isAnonymous: data.data.isAnonymous || data.data.is_anonymous || false,
          is_anonymous: data.data.isAnonymous || data.data.is_anonymous || false,
          createdAt: data.data.createdAt || data.data.created_at || new Date().toISOString(),
          created_at: data.data.createdAt || data.data.created_at || new Date().toISOString()
        }
        setReviews(prev => [newReview, ...prev])
        
        // Remove the reviewed property from applications list immediately
        const propertyId = (application.property as any)?._id || (application.property as any)?.id || application.property
        const propertyIdStr = propertyId?.toString()
        setApplications(prev => prev.filter(app => {
          const appPropertyId = (app.property as any)?._id || (app.property as any)?.id || (app as any).property_id
          return appPropertyId?.toString() !== propertyIdStr
        }))
      }

      toast.success('Review submitted successfully!')
      setShowReviewForm(false)
      resetForm()
      
      // Reload data to ensure everything is in sync
      setTimeout(() => {
        loadData()
      }, 500)
    } catch (error) {
      console.error('Error submitting review:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to submit review')
    }
  }

  // Reset form
  const resetForm = () => {
    setSelectedApplication('')
    setRating(5)
    setTitle('')
    setReviewText('')
    setIsAnonymous(false)
  }

  // Get rating stars
  const getRatingStars = (rating: number) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating)
  }

  // Filter reviews
  const filteredReviews = reviews.filter(review => {
    // Normalize reviewer ID - handle both object and string formats
    const reviewerId = review.reviewer 
      ? (typeof review.reviewer === 'object' 
          ? ((review.reviewer as any)?._id || (review.reviewer as any)?.id)
          : review.reviewer)
      : review.client_id
    
    // Normalize current user ID
    const currentUserId = user?.id || (user as any)?._id || clientId
    
    if (filter === 'all') return true
    if (filter === 'my_reviews') {
      // Compare normalized IDs
      const reviewerIdStr = reviewerId?.toString()
      const currentUserIdStr = currentUserId?.toString()
      return reviewerIdStr === currentUserIdStr
    }
    if (filter === 'public_reviews') return !review.isAnonymous && !review.is_anonymous
    return true
  })

  // Calculate stats
  const stats = {
    total: reviews.length,
    averageRating: reviews.length > 0 
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
      : 0,
    anonymous: reviews.filter(r => r.isAnonymous || r.is_anonymous).length,
    public: reviews.filter(r => !r.isAnonymous && !r.is_anonymous).length
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Review & Rating System</h2>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Reviews</option>
            <option value="my_reviews">My Reviews</option>
            <option value="public_reviews">Public Reviews</option>
          </select>
          <button
            onClick={() => {
              if (applications.length === 0) {
                toast.error('You have no approved rental applications to review. You can only review properties you have rented.')
                return
              }
              if (showReviewForm) {
                setShowReviewForm(false)
                resetForm()
              } else {
                setShowReviewForm(true)
              }
            }}
            disabled={applications.length === 0 && !showReviewForm}
            className={`px-4 py-2 rounded-md transition-colors ${
              applications.length === 0 && !showReviewForm
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : showReviewForm
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={applications.length === 0 && !showReviewForm ? 'You need an approved rental application to write a review' : showReviewForm ? 'Close review form' : 'Write a review for a property you have rented'}
          >
            {showReviewForm ? 'Cancel' : 'Write Review'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Total Reviews</h3>
          <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
          <p className="text-sm text-gray-500">All time</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Average Rating</h3>
          <p className="text-3xl font-bold text-yellow-600">{stats.averageRating.toFixed(1)}</p>
          <p className="text-sm text-gray-500">{getRatingStars(Math.round(stats.averageRating))}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Public Reviews</h3>
          <p className="text-3xl font-bold text-green-600">{stats.public}</p>
          <p className="text-sm text-gray-500">Visible to others</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Anonymous</h3>
          <p className="text-3xl font-bold text-purple-600">{stats.anonymous}</p>
          <p className="text-sm text-gray-500">Private reviews</p>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Reviews</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading reviews...</p>
          </div>
        ) : filteredReviews.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredReviews.map((review) => {
              const reviewId = review.id || (review as any)._id || `review-${Math.random()}`
              return (
              <div key={reviewId} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-medium text-gray-900">{review.title}</h4>
                      <span className="text-yellow-500">{getRatingStars(review.rating)}</span>
                      {(review.isAnonymous || review.is_anonymous) && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          Anonymous
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{review.comment || (review as any).review_text}</p>
                    
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>Property: {review.property?.title || 'Unknown'}</p>
                      <p>Landlord: {review.is_anonymous ? 'Anonymous' : review.landlord?.email || 'Unknown'}</p>
                      <p>Date: {new Date((review as any).createdAt || (review as any).created_at || new Date()).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              )
            })}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No reviews found</p>
            <p className="text-sm">Write your first review to see it here</p>
          </div>
        )}
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Write a Review</h3>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property *
                  </label>
                  <select
                    value={selectedApplication}
                    onChange={(e) => setSelectedApplication(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a property</option>
                    {applications.length === 0 ? (
                      <option value="" disabled>No approved rental applications available</option>
                    ) : (
                      applications.map((application) => {
                        const appId = application.id || (application as any)._id
                        return (
                          <option key={appId} value={appId}>
                            {application.property?.title || 'Property'} - {application.property?.location || 'Location'}
                          </option>
                        )
                      })
                    )}
                  </select>
                  {applications.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      You can only review properties from your approved rental applications.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rating *
                  </label>
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className={`text-2xl ${
                          star <= rating ? 'text-yellow-500' : 'text-gray-300'
                        }`}
                      >
                        ⭐
                      </button>
                    ))}
                    <span className="text-sm text-gray-500 ml-2">
                      {rating} out of 5
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Brief summary of your experience"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Text *
                  </label>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Share your detailed experience with this property and landlord..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="anonymous"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="anonymous" className="ml-2 block text-sm text-gray-700">
                    Submit anonymously
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={submitReview}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Submit Review
                  </button>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 

